import React, { FCC } from 'react'
import styled from 'styled-components'

import { DocumentHead, EmptyState, LoadingState } from '@sketch/components'
import { WorkspaceMinimalFragment } from '@sketch/gql-types'

import { useRecentlyOpenedShareIds } from '../../../shares/hooks/useRecentlyOpenedSharesStore'
import { useRecentlyOpenedSharesQuery } from '../../../shares/operations/useRecentlyOpenedSharesQuery'
import { DocumentItemsLayout } from '../../../shares/components/DocumentItemsLayout'
import { DocumentItemDropdown } from '../../../shares/components/DocumentItemDropdown'

const TITLE = 'Recently Opened'

const EmptyStateWrapper = styled.div`
  padding: 48px 24px;
  text-align: center;
`

interface RecentlyOpenedViewProps {
  workspaceId: string
  workspace: WorkspaceMinimalFragment
}

/**
 * Renders the user's recently opened shares for the current workspace.
 *
 * The list of share IDs is sourced from localStorage (see
 * `useRecentlyOpenedShareIds`). Metadata for all IDs is fetched in a single
 * network request via the aliased multi-field workaround in
 * `useRecentlyOpenedSharesQuery`. Deleted/inaccessible shares are filtered out
 * of the rendered list AND pruned from localStorage automatically.
 */
export const RecentlyOpenedView: FCC<RecentlyOpenedViewProps> = ({
  workspaceId,
  workspace,
}) => {
  const ids = useRecentlyOpenedShareIds(workspaceId)
  const { shares, loading, error } = useRecentlyOpenedSharesQuery(
    ids,
    workspaceId
  )

  if (loading && shares.length === 0) {
    return (
      <>
        <DocumentHead title={TITLE} />
        <LoadingState />
      </>
    )
  }

  if (error || shares.length === 0) {
    return (
      <>
        <DocumentHead title={TITLE} />
        <EmptyStateWrapper>
          <EmptyState
            title={
              error ? "Couldn't load documents" : 'No documents opened recently'
            }
            description={
              error
                ? 'Please try again later.'
                : 'Documents you open will appear here.'
            }
          />
        </EmptyStateWrapper>
      </>
    )
  }

  return (
    <>
      <DocumentHead title={TITLE} />
      <DocumentItemsLayout
        workspaceId={workspaceId}
        shares={shares}
        totalCount={shares.length}
        onLoadMore={() => Promise.resolve()}
        renderDropdown={share => (
          <DocumentItemDropdown
            share={share}
            workspaceIdentifier={workspaceId}
            workspace={workspace}
          />
        )}
        canDropShares={false}
      />
    </>
  )
}
